import { Link } from "react-router";
import { Alerta, Badge, Button, DataList, Dialog, MetricCard, Row, Select, Spinner, Stack } from "@/components";
import type { Venta } from "@/lib/api";
import { formatFecha, formatMoneda } from "@/lib/format";
import { ETIQUETA_ESTADO_VENTA, montoPositivo, nombreUnidades, OPCIONES_FILTRO_ESTADO_VENTA, opcionesComprador, TODOS, TONO_ESTADO_VENTA } from "@/lib/ventas";
import { FormularioVenta } from "./FormularioVenta";
import { useVentasTab } from "./useVentasTab";

export function VentasTab() {
  const t = useVentasTab();
  const tot = t.totales;

  const columnas = [
    { clave: "numero", titulo: "Nº", render: (v: Venta) => String(v.numero) },
    { clave: "fecha", titulo: "Fecha", render: (v: Venta) => formatFecha(v.fecha) },
    { clave: "unidades", titulo: "Unidades", render: (v: Venta) => nombreUnidades(v.unidades) },
    { clave: "comprador", titulo: "Comprador", render: (v: Venta) => v.comprador.nombre },
    { clave: "precio", titulo: "Precio", render: (v: Venta) => formatMoneda(v.precio, v.moneda_cuenta) },
    { clave: "cobrado", titulo: "Cobrado", render: (v: Venta) => formatMoneda(v.cobrado, v.moneda_cuenta) },
    {
      clave: "saldo",
      titulo: "Saldo",
      render: (v: Venta) => (
        <Stack espacio={1}>
          <span>{formatMoneda(v.saldo, v.moneda_cuenta)}</span>
          {montoPositivo(v.vencido) && <span className="text-chico text-peligro">vencido {formatMoneda(v.vencido, v.moneda_cuenta)}</span>}
        </Stack>
      ),
    },
    { clave: "estado", titulo: "Estado", render: (v: Venta) => <Badge tono={TONO_ESTADO_VENTA[v.estado]}>{ETIQUETA_ESTADO_VENTA[v.estado]}</Badge> },
  ];

  const acciones = (v: Venta) => (
    <Button variante="secundario" tamanio="sm" aria-label={`Ver venta ${v.numero}`} onClick={() => t.verFicha(v.id)}>
      Ver
    </Button>
  );

  const opcionesCompradorFiltro = [{ valor: TODOS, etiqueta: "Todos los compradores" }, ...opcionesComprador(t.compradores)];
  const hayVencido = tot !== null && (montoPositivo(tot.USD.vencido) || montoPositivo(tot.ARS_CAC.vencido));

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Ventas</h1>
        <Row envolver espacio={2}>
          <Link
            to="/compradores"
            className="inline-flex h-11 items-center justify-center rounded-control border border-borde-fuerte bg-superficie px-4 font-medium text-texto transition-colors hover:bg-superficie-2"
          >
            Compradores
          </Link>
          {t.esAdmin && <Button onClick={t.abrirNuevo}>Nueva venta</Button>}
        </Row>
      </Row>

      {tot && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            titulo="Vendido"
            valor={formatMoneda(tot.USD.precio, "USD")}
            detalle={montoPositivo(tot.ARS_CAC.precio_base) ? `+ ${formatMoneda(tot.ARS_CAC.precio_base, "ARS")} en pesos al índice de origen` : undefined}
          />
          <MetricCard titulo="Cobrado" valor={formatMoneda(tot.cobrado_usd, "USD")} detalle="Todos los cobros, pasados a USD" />
          <MetricCard titulo="Saldo" valor={formatMoneda(tot.USD.saldo, "USD")} detalle={montoPositivo(tot.ARS_CAC.saldo) ? `+ ${formatMoneda(tot.ARS_CAC.saldo, "ARS")} en pesos CAC` : undefined} />
          <MetricCard
            titulo="Vencido"
            valor={formatMoneda(tot.USD.vencido, "USD")}
            detalle={montoPositivo(tot.ARS_CAC.vencido) ? `+ ${formatMoneda(tot.ARS_CAC.vencido, "ARS")} en pesos CAC` : undefined}
            className={hayVencido ? "text-peligro" : undefined}
          />
        </div>
      )}

      <Row envolver espacio={3} alinear="fin">
        <Select etiqueta="Estado" opciones={OPCIONES_FILTRO_ESTADO_VENTA} valor={t.filtros.estado} onCambio={(v) => t.cambiarFiltro("estado", v)} className="min-w-44" />
        <Select etiqueta="Comprador" opciones={opcionesCompradorFiltro} valor={t.filtros.compradorId} onCambio={(v) => t.cambiarFiltro("compradorId", v)} className="min-w-52" />
        {t.hayFiltros && (
          <Button variante="fantasma" onClick={t.limpiarFiltros}>
            Limpiar filtros
          </Button>
        )}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList etiqueta="Ventas" columnas={columnas} filas={t.items} claveFila={(v) => v.id} acciones={acciones} vacio="Todavía no hay ventas cargadas." />
      )}

      <Dialog abierto={t.abierto} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Nueva venta">
        <FormularioVenta
          form={t.form}
          cambiar={t.cambiar}
          errores={t.errores}
          compradores={t.compradores}
          unidades={t.unidadesDisponibles}
          edicion={false}
          onSubmit={(e) => void t.guardar(e)}
          onCancelar={t.cerrar}
          guardando={t.guardando}
          error={t.errorGuardar}
        />
      </Dialog>
    </Stack>
  );
}
